import { useEffect, useState, createContext } from "react";
import {
  getProjects,
  type ProjectType,
  type GetProjectsParams,
} from "./getProjects";
import { Languages } from "./constants/Languages";
import { ReadOnlySourceCode } from "./ReadOnlySourceCode";
import { Loading } from "./Loading";

export type ProjectsPageProps = {
  currentUser: {
    id: string;
    hasGithubProvider: boolean;
  } | null;
  locale: LocaleType;
  variant: "featured" | "myCodes" | "favorites";
};

export const ProjectsPageLocaleContext = createContext<LocaleType | null>(null);

export const ProjectsPage = ({
  currentUser,
  locale,
  variant,
}: ProjectsPageProps) => {
  const limit = 10;
  const [isLoadingFirst, setIsLoadingFirst] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const isLoading = isLoadingFirst || isLoadingMore;

  // APIがfetchしたデータ
  const [projectsCount, setProjectsCount] = useState<number | null>(null);
  const [projects, setProjects] = useState<ProjectType[]>([]);

  // API呼び出しに渡すパラメータ
  const [language, setLanguage] = useState<string | null>(null);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [formInputs, setFormInputs] = useState<{ searchText: string }>({
    searchText: "",
  });
  const [offset, setOffset] = useState(0);

  // 無限スクロールのトリガー要素
  const [observedTargetElement, setObservedTargetElement] =
    useState<Element | null>(null);

  // offsetを0に戻して新しい条件でAPIを叩く
  useEffect(() => {
    setIsLoadingFirst(true);
    setOffset(0);

    const searchParams: GetProjectsParams = {
      limit,
      offset: 0, // この時点ではまだoffsetが変わっていないので直接指定
      language: language || undefined,
      // modelの仕様上`favorites`もonly_meフラグをつける
      only_me: variant === "myCodes" || variant === "favorites",
      only_featured: variant === "featured",
      only_favorite: variant === "favorites",
      search_text: searchText || undefined,
    };

    getProjects(searchParams)
      .then((response) => {
        setProjectsCount(response.count);
        setProjects(response.projects);
      })
      .catch((error) => {
        throw error;
      })
      .finally(() => {
        setIsLoadingFirst(false);
      });
  }, [variant, language, searchText]);

  // 無限スクロール
  const observer = new IntersectionObserver(([entry]) => {
    // しきい値(0)を跨ぐと発火するので、0を超えた時のみ処理を走らせる
    if (!entry.isIntersecting) return;
    if (isLoading) return;

    // 読み込み済みのもの以上に対象が存在したらoffsetをずらしてAPIを叩く
    if (Number(projectsCount) > projects.length) {
      setIsLoadingMore(true);
      const newOffset = offset + limit;
      setOffset(newOffset);

      const searchParams: GetProjectsParams = {
        limit,
        offset: newOffset,
        language: language || undefined,
        // modelの仕様上`favorites`もonly_meフラグをつける
        only_me: variant === "myCodes" || variant === "favorites",
        only_featured: variant === "featured",
        only_favorite: variant === "favorites",
        search_text: searchText || undefined,
      };

      getProjects(searchParams)
        .then((response) => {
          setProjectsCount(response.count);
          setProjects([...projects, ...response.projects]);
        })
        .catch((error) => {
          throw error;
        })
        .finally(() => {
          setIsLoadingMore(false);
        });
    }
  });

  // 無限スクロールの監視対象が変化したらobserver再起動
  useEffect(() => {
    if (!observedTargetElement) return; // 監視対象がいなければ抜ける

    observer.observe(observedTargetElement);
    return () => observer.disconnect();
  }, [observedTargetElement]);

  return (
    <>
      <ProjectsPageLocaleContext.Provider value={locale}>
        <div className="container">
          <ul className="nav nav-tabs" role="tablist">
            <li className={`${variant === "featured" ? "active" : ""}`}>
              <a href="/projects/featured">
                <div style={{ display: "inline-block" }}>
                  {i18n[locale].featured}
                </div>
              </a>
            </li>
            <li className={`${variant === "myCodes" ? "active" : ""}`}>
              <a
                href="/projects"
                onClick={(event) => {
                  if (!currentUser) {
                    event.preventDefault();
                    alert(i18n[locale].askSignIn);
                  }
                }}
              >
                <div style={{ display: "inline-block" }}>
                  {i18n[locale].myCodes}
                </div>
              </a>
            </li>
            <li className={`${variant === "favorites" ? "active" : ""}`}>
              <a
                href="/projects/favorites"
                onClick={(event) => {
                  if (!currentUser) {
                    event.preventDefault();
                    alert(i18n[locale].askSignIn);
                  }
                }}
              >
                <div style={{ display: "inline-block" }}>
                  {i18n[locale].favorites}
                </div>
              </a>
            </li>
            <li>
              <a
                href="/projects/gists"
                onClick={(event) => {
                  if (!currentUser?.hasGithubProvider) {
                    event.preventDefault();
                    alert(i18n[locale].askSignInGithub);
                  }
                }}
              >
                <div style={{ display: "inline-block" }}>Gists</div>
              </a>
            </li>
          </ul>
          <br />
          <div>
            <label
              className={`btn btn-primary ${
                language === null ? "active" : ""
              }`.trimEnd()}
              onClick={() => {
                setLanguage(null);
              }}
            >
              All
            </label>{" "}
            {Languages.sort((a, b) => a.label.localeCompare(b.label)).map(
              ({ id, label }) => (
                <label
                  className={`btn btn-success ${
                    language === id ? "active" : ""
                  }`.trimEnd()}
                  key={id}
                  onClick={() => {
                    setLanguage(id);
                  }}
                >
                  {label}
                </label>
              )
            )}
          </div>
          <br />
          {variant !== "featured" && (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                setSearchText(formInputs.searchText);
              }}
            >
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search"
                  onChange={(e) =>
                    setFormInputs({ ...formInputs, searchText: e.target.value })
                  }
                />
                <span className="input-group-btn">
                  <button className="btn btn-default" type="submit">
                    <div className="glyphicon-search glyphicon" />
                  </button>
                </span>
              </div>
            </form>
          )}

          {isLoadingFirst ? (
            <Loading />
          ) : (
            <div>{projectsCount ?? 0} records found.</div>
          )}
          <div>
            {projects.map((project, i) => {
              return (
                <>
                  <ReadOnlySourceCode
                    key={project.uid}
                    loginUser={{ id: currentUser?.id || null }}
                    project={{
                      uid: project.uid,
                      id: project.id,
                      title: project.title,
                      language: project.language,
                      createdAt: new Date(project.created_at),
                      share: project.share === "public" ? "public" : "private",
                      isFavorite: project.favorite === "true",
                      isFeatured: project.featured === "true",
                      sourceCode: project.source_files?.[0]?.body || "",
                      user: project.user
                        ? {
                            id: project.user.id,
                            name: project.user.username,
                            imageUrl: project.user.image_url,
                          }
                        : null,
                    }}
                  />
                  {i === projects.length - 1 && (
                    <div ref={setObservedTargetElement} />
                  )}
                </>
              );
            })}
            {isLoadingMore && <Loading />}
          </div>
        </div>
      </ProjectsPageLocaleContext.Provider>
    </>
  );
};

type LocaleType = "ja-jp" | "en-us" | "es-es";

const i18n: {
  [key in LocaleType]: {
    myCodes: string;
    featured: string;
    favorites: string;
    askSignIn: string;
    askSignInGithub: string;
  };
} = {
  "ja-jp": {
    myCodes: "自分のコード",
    featured: "おすすめ",
    favorites: "お気に入り",
    askSignIn: "この機能はログインしてご利用ください。",
    askSignInGithub:
      "この機能はGitHubログイン又はGitHub連携後にご利用ください。",
  },
  "en-us": {
    myCodes: "My codes",
    featured: "Featured",
    favorites: "Favorite",
    askSignIn: "Please sign in to use this feature.",
    askSignInGithub:
      "Please sign in or connect account to GitHub to use this feature.",
  },
  "es-es": {
    myCodes: "Mis códigos",
    featured: "Featured",
    favorites: "Favoritos",
    askSignIn: "Please sign in to use this feature.",
    askSignInGithub:
      "Please sign in or connect account to GitHub to use this feature.",
  },
};
